import { Clinic } from '@web/csl-lib-models';
import { ClinicGlobalState } from './clinic-global.state';
import { ClinicMedicalRecordReleaseService } from '../services/clinic-medical-record-release/clinic-medical-record-release.service';
import { ClinicMessagesService } from '../services/messages/clinic-messages.service';
import { ClinicOwnerTransferService } from '../services/clinic-owner-transfer/clinic-owner-transfer.service';
import { GlobalStore } from '@web/csl-lib-store';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { pipe, switchMap, tap } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ScpUploadWorklistService } from '../services/scp-upload-worklist/scp-upload-worklist.service';
import { SurvivorClinicService } from '../services/survivor-clinic/survivor-clinic.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';

const initialState: ClinicGlobalState = {
  clinicId: null,
  clinic: null,
  unreadMessageCount: 0,
  scpWorklistQueuedCount: 0,
  pendingMedicalRecordReleaseCount: 0,
  pendingOwnerTransferRequestCount: 0,
};

export const ClinicGlobalStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync('clinicGlobalStore', withSessionStorage()),
  withMethods(
    (
      store,
      globalStore = inject(GlobalStore),
      clinicMessagesService = inject(ClinicMessagesService),
      clinicService = inject(SurvivorClinicService),
      medicalRecordReleaseService = inject(ClinicMedicalRecordReleaseService),
      messageService = inject(MessageService),
      ownerTransferService = inject(ClinicOwnerTransferService),
      scpUploadWorklistService = inject(ScpUploadWorklistService)
    ) => ({
      setLoading() {
        globalStore.setLoading();
      },
      unsetLoading() {
        globalStore.unsetLoading();
      },

      initClinic: rxMethod<number>(
        pipe(
          switchMap((clinicId: number) => {
            globalStore.setLoading();
            return clinicService.getClinic(clinicId).pipe(
              tap({
                next: (clinic: Clinic) => {
                  patchState(store, {
                    clinic: clinic,
                    clinicId: clinicId
                  });
                },
                error: (e) => {
                  console.error(e);
                  messageService.add({severity: 'error', summary: 'Error!', detail: `Error loading clinic portal. Please logout and try again.  If issue persists, please contact SurvivorLink.`, life: 5000});
                },
                finalize: () => {
                  globalStore.unsetLoading();
                },
              })
            );
          })
        )
      ),

      updateUnreadMessageCount: rxMethod<void>(
        pipe(
          switchMap(() => {
            return clinicMessagesService.updateUnreadMessageCount(store.clinicId()!).pipe(
              tap({
                next: (count: number) => {
                  patchState(store, {
                    unreadMessageCount: count,
                  });
                },
                error: (e) => {
                  console.error(e);
                },
                finalize: () => {},
              })
            );
          })
        )
      ),

      updateScpWorklistQueuedCount: rxMethod<void>(
        pipe(
          switchMap(() => {
            return scpUploadWorklistService.getQueuedCount(store.clinicId()!).pipe(
              tap({
                next: (count: number) => {
                  patchState(store, {
                    scpWorklistQueuedCount: count,
                  });
                },
                error: (e) => {
                  console.error(e);
                },
                finalize: () => {},
              })
            );
          })
        )
      ),

      updatePendingMedicalRecordReleaseCount: rxMethod<void>(
        pipe(
          switchMap(() => {
            return medicalRecordReleaseService.getPendingCount(store.clinicId()!).pipe(
              tap({
                next: (count: number) => {
                  patchState(store, {
                    pendingMedicalRecordReleaseCount: count,
                  });
                },
                error: (e) => {
                  console.error(e);
                },
                finalize: () => {},
              })
            );
          })
        )
      ),

      updatePendingOwnerTransferCount: rxMethod<void>(
        pipe(
          switchMap(() => {
            return ownerTransferService.getPendingCount(store.clinicId()!).pipe(
              tap({
                next: (count: number) => {
                  patchState(store, {
                    pendingOwnerTransferRequestCount: count,
                  });
                },
                error: (e) => {
                  console.error(e);
                },
                finalize: () => {},
              })
            );
          })
        )
      ),

    })
  ),
  withMethods((store) => ({
    setIsPassportForCareClinic(value: boolean) {
      const clinic = store.clinic();
      if (clinic) {
        patchState(store, { clinic: { ...clinic, isPassportForCareClinic: value } });
      }
    }
  })),
  withHooks({
    onInit(store) {

      // Load the health record index when the clinic id is set
      toObservable(store.clinicId).subscribe({
        next: (clinicId) => {
          if(clinicId !== null) {
            store.updatePendingMedicalRecordReleaseCount();
            store.updateScpWorklistQueuedCount();
            store.updatePendingOwnerTransferCount();
            store.updateUnreadMessageCount();
          }
        },
        error: (e) => {
          const messageService = inject(MessageService);
          messageService.add({severity: 'error', summary: 'Error!', detail: `Error loading clinic health records. Please logout and try again.  If issue persists, please contact SurvivorLink.`, life: 5000});
          console.error(e);
        }
      });

    },
  })
);



